// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { JwtSummary } from './mask';

/**
 * The JWT claim reader.
 *
 * SCOPE, DELIBERATELY NARROW: leakmap reads an allowlisted claim set and links out. Signature
 * verification, HMAC cracking, token forging and a claims glossary are `tokenpeek`'s job and are not
 * duplicated here.
 *
 * WHY THIS EXISTS AT ALL: the entire Supabase discrimination is ONE CLAIM. Measured on Supabase's
 * own published demo keys, the `anon` and `service_role` tokens are structurally identical —
 * segment lengths `36,95,43`, unpadded — and differ only in `"role":"anon"` versus
 * `"role":"service_role"`. A `service_role` key is the Postgres `service_role`, which carries
 * BYPASSRLS and therefore skips every row-level-security policy you wrote.
 *
 * A JWT-ONLY IMPLEMENTATION IS NO LONGER SUFFICIENT. Supabase's current keys are
 * `sb_publishable_…` / `sb_secret_…` — plain strings with no decodable payload — with the legacy
 * JWTs documented as deprecated by the end of 2026 and both working simultaneously today. Those two
 * prefixes are first-class rules in `shapes.ts`; this file handles the legacy form.
 */

/**
 * base64url -> UTF-8.
 *
 * The alphabet translation is MANDATORY: `atob` throws `InvalidCharacterError` on BOTH `-` and `_`
 * (the commonly-cited note mentions only `_`). Padding is optional — `atob` accepts `len % 4` of 2
 * and 3 and throws at 1 — but adding it is free. Note `atob` also silently accepts ASCII whitespace,
 * so whitespace cannot be used as a validity gate.
 */
export function b64urlToUtf8(seg: string): string {
  if (seg.length % 4 === 1) throw new Error('malformed base64url segment');
  const b64 = seg.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (seg.length % 4)) % 4);
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  // fatal:true rejects a binary decoy that happens to base64-decode.
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
}

/**
 * Candidate pattern, WITH word boundaries — measured, an unbounded pattern happily matches inside
 * `XXXX<jwt>YYYY`. Tolerates `/`, `+`, `=` and the `\/`-escaped form a bundler may have written.
 */
export const JWT_CANDIDATE_RE = /\bey[A-Za-z0-9_/+-]{17,}\.ey[A-Za-z0-9_/+-]{10,}\.[A-Za-z0-9_/+=-]*/g;

/**
 * The claims we will render. EVERY OTHER CLAIM IS HIDDEN.
 *
 * This is not tidiness. The same pattern that catches a Supabase key also catches USER SESSION
 * TOKENS carrying `email`, `phone`, `sub` and `name`. Pretty-printing a whole payload into
 * `exposure-report.html` — a file the user then attaches to a ticket — would make leakmap a second
 * leak, of somebody else's personal data.
 */
const CLAIM_ALLOWLIST = new Set(['alg', 'typ', 'kid', 'iss', 'ref', 'role', 'aud', 'iat', 'exp', 'nbf']);

const MAX_PAYLOAD_BYTES = 8192;

export interface JwtReading {
  readonly summary: JwtSummary;
  /** Allowlisted claims only; every other key renders as the marker below. */
  readonly claims: Record<string, string>;
  readonly hiddenClaimCount: number;
}

export const HIDDEN_CLAIM = '⟨hidden⟩';

/**
 * Read a candidate. Returns null when it is not actually a JWT.
 *
 * `JSON.parse` THROWING IS THE FALSE-POSITIVE FILTER, NOT AN ERROR STATE. `eyJ` is only a heuristic:
 * base64 of `{"` fixes just two characters and the third is one of {I,J,K,L}, so `{"2fa"` encodes to
 * `eyIy` and other non-JSON payloads reach here routinely. A SyntaxError is exactly the reject
 * signal and must not be surfaced as a failure.
 */
export function readJwt(token: string): JwtReading | null {
  const parts = token.replace(/\\\//g, '/').split('.');
  // 5 segments is a JWE: there is no readable payload, and producing garbage from one is worse than
  // saying so.
  if (parts.length !== 3) return null;

  let header: Record<string, unknown>;
  let payload: Record<string, unknown>;
  try {
    const h = b64urlToUtf8(parts[0]!);
    const p = b64urlToUtf8(parts[1]!);
    if (p.length > MAX_PAYLOAD_BYTES) return null;
    header = JSON.parse(h) as Record<string, unknown>;
    payload = JSON.parse(p) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (typeof header !== 'object' || header === null || typeof payload !== 'object' || payload === null) return null;

  const claims: Record<string, string> = {};
  let hidden = 0;
  const record = (k: string, v: unknown) => {
    if (!CLAIM_ALLOWLIST.has(k)) {
      hidden++;
      return;
    }
    if (k === 'iat' || k === 'exp' || k === 'nbf') {
      // Masked to date granularity — a second-precision timestamp is a correlation handle.
      const n = typeof v === 'number' ? v : Number(v);
      claims[k] = Number.isFinite(n) ? new Date(n * 1000).toISOString().slice(0, 10) : String(v);
      return;
    }
    claims[k] = typeof v === 'string' ? v : JSON.stringify(v);
  };
  for (const [k, v] of Object.entries(header)) record(k, v);
  for (const [k, v] of Object.entries(payload)) record(k, v);

  const role = typeof payload['role'] === 'string' ? (payload['role'] as string) : undefined;
  const ref = typeof payload['ref'] === 'string' ? (payload['ref'] as string) : undefined;
  const iss = typeof payload['iss'] === 'string' ? (payload['iss'] as string) : undefined;
  const exp = typeof payload['exp'] === 'number' ? (payload['exp'] as number) : undefined;
  const expiredOn = exp !== undefined && exp * 1000 < Date.now() ? new Date(exp * 1000).toISOString().slice(0, 10) : undefined;

  return {
    summary: { segments: 3, role, ref, iss, expiredOn, bypassRls: role === 'service_role' },
    claims,
    hiddenClaimCount: hidden,
  };
}

/**
 * The copy for each Supabase role. Written out here rather than in the UI because the `anon` wording
 * is the one place this tool is most likely to be wrong in a damaging direction — it must not say
 * "safe", and it must not say "leaked" either.
 */
export function supabaseRoleAdvice(role: string | undefined): { severity: 'critical' | 'medium' | 'info'; text: string } | null {
  if (role === 'service_role') {
    return {
      severity: 'critical',
      text:
        'This is the Postgres service_role, which carries BYPASSRLS — it skips every row-level-security policy you wrote. ' +
        'Anyone who can fetch this file has full read and write access to your database. Rotate it now.',
    };
  }
  if (role === 'anon') {
    return {
      severity: 'medium',
      text:
        'An anon key in a client bundle is normal and intended — it is only as safe as your Row Level Security policies. ' +
        'leakmap runs entirely in your browser and has never contacted your database, so it cannot tell you whether RLS is on. ' +
        'If you have any table without an RLS policy, this key is a full public read of that table.',
    };
  }
  return null;
}

/**
 * Never display the token, and never display `eyJhbG…` as an identifier — that is a constant prefix
 * of every HS256 JWT and identifies nothing. Identify by env key, role and project ref.
 */
export function jwtIdentity(envKey: string | undefined, reading: JwtReading): string {
  const bits = [envKey ?? 'JWT'];
  if (reading.summary.role) bits.push(`role=${reading.summary.role}`);
  if (reading.summary.ref) bits.push(`ref=${reading.summary.ref}`);
  return bits.join(' · ');
}
